import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { enqueueSnackbar } from 'notistack';
// components
import { Card, CardContent, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import { FileIcon, DownloadIcon, ImageIcon } from 'lucide-react';
import ModalLightbox from 'src/@/components/shared/ModalLightbox';
import { downloadMedia } from 'src/services/chat/downloadMedia';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/@/components/ui/tooltip';
import v1_actionDownloadMediaWhatsapp from 'src/actions/v1/whatsapp/v1_actionDownloadMediaWhatsapp';

ChatMedia.propTypes = {
    messages: PropTypes.array,
    onClickDownload: PropTypes.func,
    sessao: PropTypes.string
};

const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export default function ChatMedia({ messages, onClickDownload, sessao }) {
    const { t } = useTranslation('chat');
    const [activeTab, setActiveTab] = useState('imagens');
    const [openModalImage, setOpenModalImage] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const imageRef = useRef(null);
    const { enqueueSnackbar } = useSnackbar();

    // Filtrar mensagens por tipo de mídia
    const imagens = messages?.filter(msg => msg.type === 'image') || [];
    const documentos = messages?.filter(msg => msg.type === 'document') || [];
    const links = messages?.filter(msg => msg.type === 'link') || [];

    const handleDownload = async (message) => {
        try {
            setDownloadingId(message.id);
            let querys = `?sessao=${message.session}&msgId=${message.id}&canal=${message.platform}`
            const response = await v1_actionDownloadMediaWhatsapp(querys);
            if(response.status === 200) {
                const link = document.createElement('a');
                link.href = `data:${message.mimetype};base64,${response.data.data.base64}`;
                link.download = message.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            enqueueSnackbar(t('media.downloadError'), { variant: 'error' });
            console.log('Erro ao baixar arquivo', error)
        } finally {
            setDownloadingId(null);
        }
    };

    const handleImageClick = async (img) => {
        try {
            setLoadingDownload(true);
            const imageData = await downloadMedia(img, sessao);
            setSelectedImage(imageData);
            setOpenModalImage(true);
        } catch (error) {
            enqueueSnackbar(t('media.loadImageError'), { variant: 'error' });
        } finally {
            setLoadingDownload(false);
        }
    };

    return (
        <div>
                <Tabs defaultValue="imagens" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="imagens">
                            {t('media.photos')} ({imagens.length})
                        </TabsTrigger>
                        <TabsTrigger value="documentos">
                            {t('media.documents')} ({documentos.length})
                        </TabsTrigger>
    
                    </TabsList>

                    <TabsContent value="imagens">
                        <ScrollArea className="h-[300px] w-full p-4">
                            {imagens.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full space-y-2">
                                    <ImageIcon className="w-12 h-12 text-gray-300" />
                                    <p className="text-gray-500 text-center">
                                        {t('media.noImages')}
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-3 gap-4">
                                    {imagens.map((img, index) => (
                                        <TooltipProvider key={img._id}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div 
                                                        className="relative aspect-square cursor-pointer"
                                                        onClick={() => handleImageClick(img)}
                                                    >
                                                        {loadingDownload ? (
                                                            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-md">
                                                                <span className="loading loading-spinner loading-md" />
                                                            </div>
                                                        ) : (
                                                            <img
                                                                ref={imageRef}
                                                                src={`data:${img.mimetype};base64,${img.body}`}
                                                                alt={`${t('media.image')} ${index + 1}`}
                                                                className="w-full h-full object-cover rounded-md hover:opacity-90 transition-opacity"
                                                            />
                                                        )}
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent side="bottom" className="space-y-1">
                                                    <div className="flex flex-col text-xs space-y-1">
                                                        <div className="flex items-center flex-col gap-2">
                                                            <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                                                                img.fromMe ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                                            }`}>
                                                                {img.fromMe ? t('media.attendant') : t('media.client')}
                                                            </span>
                                                            <span className="text-gray-500">
                                                                {new Date(img.date).toLocaleString('pt-BR', {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: '2-digit',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                            </span>
                                                        </div>
                                                        <span className="text-gray-500">
                                                            {t('media.size')}: {formatFileSize(img.size)}
                                                        </span>
                                                    </div>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    ))}
                                </div>
                            )}
                        </ScrollArea>
                    </TabsContent>

                    <TabsContent value="documentos">
                        <ScrollArea className="h-[300px] w-full p-2">
                            {documentos.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full space-y-2">
                                    <FileIcon className="w-12 h-12 text-gray-300" />
                                    <p className="text-gray-500 text-center">
                                        {t('media.noDocuments')}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {documentos.map((doc) => (
                                        <div 
                                            key={doc?._id} 
                                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className="p-2 bg-blue-100 rounded-lg">
                                                    <FileIcon className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-xs">{doc?.filename?.length > 20 ? `${doc?.filename?.substring(0, 20)}...` : doc?.filename}</span>
                                                    <span className="text-sm text-gray-500">
                                                        {formatFileSize(doc?.size)}
                                                    </span>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => handleDownload(doc)}
                                                className="p-2 text-gray-600 hover:text-blue-600 rounded-full hover:bg-blue-50"
                                                disabled={downloadingId === doc?.id}
                                            >
                                                {downloadingId === doc?.id ? (
                                                    <span className="loading loading-spinner loading-sm" />
                                                ) : (
                                                    <DownloadIcon className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </ScrollArea>
                    </TabsContent>

                </Tabs>

                <ModalLightbox
                    open={openModalImage}
                    onClose={() => setOpenModalImage(false)}
                    src={selectedImage}
                />
     </div>
    );
} 